import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import axiosInstance from "@/lib/axios";
import { MedicationRequest } from "@curai/js-fhir-tools";

interface RenewablePrescriptionsParams {
  patientId: string;
  motherEncounterId?: string;
  enabled?: boolean;
}

export interface RenewablePrescriptionForDrug {
  medicationRequest: MedicationRequest;
  isDuplicate: boolean;
}

export interface RenewablePrescriptionsForDrug {
  drugIdentifier: string;
  drugName: string;
  isObsolete: boolean;
  renewablePrescriptions: RenewablePrescriptionForDrug[];
}

interface RenewablePrescriptionsResponse {
  prescriptionsForDrugs: RenewablePrescriptionsForDrug[];
}

export const useRenewablePrescriptions = ({
  patientId,
  motherEncounterId,
  enabled = true,
}: RenewablePrescriptionsParams) => {
  return useApi<RenewablePrescriptionsResponse>({
    queryOptions: {
      enabled: Boolean(motherEncounterId && patientId && enabled),
      refetchOnMount: "always",
      queryFn: async () => {
        const response =
          await axiosInstance.get<RenewablePrescriptionsResponse>(
            "/rx/refills/renewable-prescriptions",
            {
              params: {
                patientId,
                encounterId: motherEncounterId,
                filterObsolete: false,
              },
            },
          );

        return response.data;
      },
      queryKey: QueryKeys.Patient.Chart.Prescriptions.RENEWABLE_PRESCRIPTIONS({
        patientId,
        encounterId: motherEncounterId as string,
      }),
      select: (data) => {
        return {
          ...data,
          prescriptionsForDrugs: data.prescriptionsForDrugs.map(
            (prescriptionsForDrug) => ({
              ...prescriptionsForDrug,
              renewablePrescriptions:
                prescriptionsForDrug.renewablePrescriptions.map(
                  (renewablePrescription) => {
                    return {
                      ...renewablePrescription,
                      medicationRequest: new MedicationRequest(
                        renewablePrescription.medicationRequest,
                      ),
                    };
                  },
                ),
            }),
          ),
        };
      },
    },
  });
};
